/**
 * Suppliers List Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSuppliers hook.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Loader2, Plus, ChevronDown, Truck, UserPlus, CheckCircle2, Network } from 'lucide-react';
import { SectionTourTrigger } from '../components/tour/SectionTourTrigger';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { coreAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useRegisterPageRefresh } from '../contexts/PageRefreshContext';
import {
  useSuppliers,
  SupplierMap,
  SupplierGrid,
  SearchFilterBar,
  AddSupplierGroupModal,
  AddApprovedSupplierModal,
} from './suppliers-list';
import type { AddTarget } from './suppliers-list';

interface LogisticsFormData {
  company_name: string;
  address: string;
  postcode: string;
  phone: string;
  email: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
}

const INITIAL_LOGISTICS_FORM: LogisticsFormData = {
  company_name: '', address: '', postcode: '', phone: '', email: '',
  contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '',
};

export default function Suppliers() {
  const {
    navigate,
    loading,
    error,
    suppliers,
    filteredSuppliers,
    mapLocations,
    currentTab,
    setCurrentTab,
    setSelectedGroupName,
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    riskCounts,
    questionnaireCounts,
    otifBandCounts,
    siteOptions,
    commodityGroupOptions,
    commodityTypeOptions,
    supplierCodeOptions,
    searchRef,
    searchQuery,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    paginatedSuppliers,
    fetchData,
  } = useSuppliers();
  useRegisterPageRefresh(fetchData);
  const { user } = useAuth();
  // CR-6654 round 2: the active view scope decides which customer a new
  // supplier is linked to — see resolveCustomerTarget below.
  const { selectedSiteId, selectedCompanyId, selectedSiteName, selectedCompanyName, selectedGroupId } = useView();
  const { isFeatureEnabled, isMenuItemVisible } = useMenuSettings();
  // Logistics company option is only relevant when Call Off is active. The flag is now
  // driven by per-supplier SupplierCustomerRelationship.call_off_enabled.
  const showLogisticsOption = isMenuItemVisible('callOff') && isFeatureEnabled('switch_call_off_tab');

  // Logistics modal state
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [logisticsForm, setLogisticsForm] = useState<LogisticsFormData>(INITIAL_LOGISTICS_FORM);
  const [logisticsSaving, setLogisticsSaving] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  // CR-6654: the customer target for the Add Approved Supplier modal, resolved
  // when it opens so the modal can name it and submit exactly what it showed.
  const [addTarget, setAddTarget] = useState<AddTarget | null>(null);
  const [addTargetLoading, setAddTargetLoading] = useState(false);

  useEffect(() => {
    if (!showAddSupplierModal && !showAddGroupModal) return;
    let cancelled = false;
    setAddTargetLoading(true);
    resolveCustomerTarget()
      .then((target) => { if (!cancelled) setAddTarget(target); })
      .catch((err: unknown) => {
        console.error('Failed to resolve the supplier target:', err);
        if (!cancelled) setAddTarget({ ids: [], label: '' });
      })
      .finally(() => { if (!cancelled) setAddTargetLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddSupplierModal, showAddGroupModal, selectedSiteId, selectedCompanyId, selectedGroupId]);

  /**
   * Resolve which customer company/companies a new supplier will be linked to,
   * with a label naming them for the user.
   *
   * CR-6654 round 2: this mirrors the *view scope* the directory reads on
   * (site → company → group), not the requester's own profile company — for a
   * helix admin those differ, and linking to the helix company produced a
   * supplier no customer directory could show. Empty ids mean the scope can't
   * resolve to a customer, so the caller refuses rather than write a dead row.
   *
   * Round 3: the modal names the target, so it is resolved once when the modal
   * opens and those same ids are submitted — what the user is told and where
   * the supplier lands cannot drift apart. At group scope there is no single
   * company to name, so ``options`` is returned instead and the user picks.
   *
   * CR-6680: at group scope every viewer (customer admin as well as internal)
   * gets the ``options`` list, so a supplier relevant to several sites is added
   * across all of them in one pass. Own company is only the *fallback* when a
   * single site resolves, so single-site buyers are unaffected. The list is
   * scoped to the selected group — the "All Companies" rollup shows every site,
   * a specific group only its own; this narrows, never widens, a user's set.
   *
   * @returns The resolved target, or the options to choose from at group scope
   */
  const resolveCustomerTarget = async (): Promise<AddTarget> => {
    if (selectedSiteId) {
      return { ids: [selectedSiteId], label: selectedSiteName || 'the selected site' };
    }
    if (selectedCompanyId) {
      return { ids: [selectedCompanyId], label: selectedCompanyName || 'the selected company' };
    }
    // Group scope: narrow to the selected group; sites sit in leaf sub-groups so
    // a parent/root group matches none — then keep the full accessible set.
    const resp = await coreAPI.getAccessibleCompanies();
    const accessible = (resp.data?.results || []).filter((c) => c.company_type === 'customer');
    const inGroup = selectedGroupId ? accessible.filter((c) => c.group === selectedGroupId) : [];
    const customers = inGroup.length ? inGroup : accessible;
    if (customers.length > 1) {
      return { ids: [], label: '', options: customers.map((c) => ({ id: c.id, name: c.name })) };
    }
    if (customers.length === 1) {
      return { ids: [customers[0].id], label: customers[0].name };
    }
    // No accessible customer company resolved. A customer user still has their
    // own company as a correct default; an internal (helix) viewer has none, so
    // the caller refuses rather than writing an unreachable row.
    if (user?.company_type === 'customer' && user?.company) {
      return { ids: [user.company], label: user.companyName || 'your company' };
    }
    return { ids: [], label: '' };
  };

  const handleLogisticsSubmit = async () => {
    if (!logisticsForm.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (logisticsForm.contact_email.trim() && (!logisticsForm.contact_first_name.trim() || !logisticsForm.contact_last_name.trim())) {
      toast.error('Contact first and last name are required when providing an email');
      return;
    }

    setLogisticsSaving(true);
    try {
      const { default: api } = await import('../lib/api/client');
      await api.post('/call-off/logistics/links/add-company/', {
        company_name: logisticsForm.company_name.trim(),
        address: logisticsForm.address.trim(),
        postcode: logisticsForm.postcode.trim(),
        phone: logisticsForm.phone.trim(),
        email: logisticsForm.email.trim(),
        contact_first_name: logisticsForm.contact_first_name.trim(),
        contact_last_name: logisticsForm.contact_last_name.trim(),
        contact_email: logisticsForm.contact_email.trim(),
        contact_phone: logisticsForm.contact_phone.trim(),
      });
      toast.success('Logistics company added successfully');
      setShowLogisticsModal(false);
      setLogisticsForm(INITIAL_LOGISTICS_FORM);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add logistics company';
      toast.error(message);
    } finally {
      setLogisticsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading suppliers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
   <div className="space-y-6">
        {/* Header with Add Dropdown */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Suppliers</h1>
            <p className="text-muted-foreground">
              Approved supplier directory
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SectionTourTrigger
              pageKey="suppliers"
              sectionKey="list"
              variant="link"
              label="Tour this page"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-tour="add-supplier-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/onboarding')}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Onboard New Supplier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAddSupplierModal(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Add Approved Supplier
                </DropdownMenuItem>
                {/* Mirrors the server gate (IsHelixOrCustomerAdmin): Helix
                    operators, or an admin on the customer side. Offering it to
                    anyone else would only dead-end in a 403. */}
                {(user?.company_type === 'helix'
                  || (user?.company_type === 'customer' && user?.is_company_admin)) && (
                  <DropdownMenuItem onClick={() => setShowAddGroupModal(true)}>
                    <Network className="h-4 w-4 mr-2" />
                    Add Supplier Group
                  </DropdownMenuItem>
                )}
                {showLogisticsOption && (
                  <DropdownMenuItem onClick={() => setShowLogisticsModal(true)}>
                    <Truck className="h-4 w-4 mr-2" />
                    Add Logistics Company
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content */}
        <Card className="p-0">
          <CardContent className="p-6">
            <div className="space-y-6">
              <SupplierMap
                locations={mapLocations}
                onViewDetails={(id) => navigate(`/suppliers/${id}`)}
              />
              <SupplierGrid
                suppliers={suppliers}
                filteredSuppliers={filteredSuppliers}
                paginatedSuppliers={paginatedSuppliers}
                currentTab={currentTab}
                onTabChange={(tab) => { setCurrentTab(tab); if (tab === 'groups') setSelectedGroupName(null); }}
                onNavigate={navigate}
                onSelectGroup={(groupName) => {
                  // CR-4578: When user clicks a Supplier Group card, switch to sites tab
                  // filtered by that group
                  setSelectedGroupName(groupName);
                  setCurrentTab('sites');
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                searchFilterBar={
                  <SearchFilterBar
                    searchRef={searchRef}
                    showFilters={showFilters}
                    filters={filters}
                    riskCounts={riskCounts}
                    questionnaireCounts={questionnaireCounts}
                    otifBandCounts={otifBandCounts}
                    siteOptions={siteOptions}
                    commodityGroupOptions={commodityGroupOptions}
                    commodityTypeOptions={commodityTypeOptions}
                    supplierCodeOptions={supplierCodeOptions}
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                    onSearchKeyDown={handleSearchKeyDown}
                    onClearSearch={handleClearSearch}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                    onFiltersChange={setFilters}
                  />
                }
              />
            </div>
          </CardContent>
        </Card>
          
      {/* Add Supplier Group Modal */}
      <AddSupplierGroupModal
        open={showAddGroupModal}
        onOpenChange={setShowAddGroupModal}
        onCreated={fetchData}
        supplierOptions={suppliers
          .filter((s) => (s.company_id ?? s.company) > 0)
          .map((s) => ({ id: (s.company_id ?? s.company) as number, name: s.company_name }))}
        customerTarget={
          addTarget && !addTarget.options && addTarget.ids.length
            ? { ids: addTarget.ids, label: addTarget.label }
            : null
        }
      />

      {/* Add Approved Supplier Modal (CR-6680: tile-based multi-select target) */}
      <AddApprovedSupplierModal
        open={showAddSupplierModal}
        onOpenChange={setShowAddSupplierModal}
        addTarget={addTarget}
        addTargetLoading={addTargetLoading}
        onCreated={fetchData}
      />

      {/* Add Logistics Company Modal */}
      <Dialog open={showLogisticsModal} onOpenChange={setShowLogisticsModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Add Logistics Company
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="logistics-name">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="logistics-name"
                value={logisticsForm.company_name}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Enter logistics company name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logistics-address">Address</Label>
              <Input
                id="logistics-address"
                value={logisticsForm.address}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="logistics-postcode">Postcode</Label>
                <Input
                  id="logistics-postcode"
                  value={logisticsForm.postcode}
                  onChange={(e) => setLogisticsForm(prev => ({ ...prev, postcode: e.target.value }))}
                  placeholder="Postcode"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logistics-phone">Phone</Label>
                <Input
                  id="logistics-phone"
                  value={logisticsForm.phone}
                  onChange={(e) => setLogisticsForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logistics-email">Email</Label>
              <Input
                id="logistics-email"
                type="email"
                value={logisticsForm.email}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@logistics.com"
              />
            </div>

            {/* Contact User Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Contact User (Login Account)</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="logistics-contact-first">First Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="logistics-contact-first"
                      value={logisticsForm.contact_first_name}
                      onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_first_name: e.target.value }))}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="logistics-contact-last">Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="logistics-contact-last"
                      value={logisticsForm.contact_last_name}
                      onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_last_name: e.target.value }))}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logistics-contact-email">Login Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="logistics-contact-email"
                    type="email"
                    value={logisticsForm.contact_email}
                    onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="user@logistics.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logistics-contact-phone">Phone</Label>
                  <Input
                    id="logistics-contact-phone"
                    value={logisticsForm.contact_phone}
                    onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  A login account will be created with default password: <strong>Welcome123!</strong>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogisticsModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogisticsSubmit} disabled={logisticsSaving}>
              {logisticsSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Logistics Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
